import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, Eye, EyeOff, Check } from 'lucide-react';
import { cn } from '../lib/utils';

const industries = [
  'Retail & E-commerce',
  'Professional Services',
  'Healthcare',
  'Real Estate',
  'Restaurant & Food',
  'Fitness & Wellness',
  'Home Services',
  'Automotive',
  'Education',
  'Technology',
  'Other'
];

// v2.5.866 — Aligned with LandingPage.jsx + BillingTab.jsx tier definitions.
// Single source of truth for tier pricing lives in LandingPage.jsx `plans`.
const plans = [
  { id: 'starter',    name: 'Starter',    price: '$299/mo',  features: ['CRM & Lead Pipeline', 'Basic Analytics', 'AI Content (100/mo)', '5 Team Members'] },
  { id: 'growth',     name: 'Growth',     price: '$699/mo',  features: ['Everything in Starter', 'Advanced Analytics', 'AI Content (500/mo)', 'Priority Support', '25 Team Members'], popular: true },
  { id: 'enterprise', name: 'Enterprise', price: 'Custom',   features: ['Everything in Growth', 'Unlimited AI', 'White-Label & Multi-Tenant', 'Dedicated Account Manager'] },
];

export const SignupPage = () => {
  const [formData, setFormData] = useState({
    full_name: '',
    business_name: '',
    email: '',
    password: '',
    phone: '',
    industry: '',
    plan: 'starter'
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await register(formData);
    
    if (result.success) {
      navigate('/onboarding');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-off-white py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <Card className="shadow-lg" data-testid="signup-form">
          <CardHeader className="text-center">
            <Link to="/" className="inline-flex items-center justify-center gap-2 mb-4">
              <img src="/konnect-logo.png" alt="Konnect" className="h-10 w-auto object-contain" />
            </Link>
            <CardTitle className="text-2xl font-heading">Create your account</CardTitle>
            <CardDescription>Start your 14-day free trial. No credit card required.</CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert variant="destructive" className="mb-4" data-testid="signup-error">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Personal Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="full_name">Full Name *</Label>
                  <Input
                    id="full_name"
                    placeholder="John Smith"
                    value={formData.full_name}
                    onChange={(e) => handleChange('full_name', e.target.value)}
                    required
                    data-testid="signup-fullname-input"
                    className="focus-ring"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="business_name">Business Name</Label>
                  <Input
                    id="business_name"
                    placeholder="Acme Inc."
                    value={formData.business_name}
                    onChange={(e) => handleChange('business_name', e.target.value)}
                    data-testid="signup-business-input"
                    className="focus-ring"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@company.com"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    required
                    data-testid="signup-email-input"
                    className="focus-ring"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    data-testid="signup-phone-input"
                    className="focus-ring"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="password">Password *</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Create a strong password"
                      value={formData.password}
                      onChange={(e) => handleChange('password', e.target.value)}
                      required
                      minLength={6}
                      data-testid="signup-password-input"
                      className="focus-ring pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="industry">Industry</Label>
                  <Select value={formData.industry} onValueChange={(val) => handleChange('industry', val)}>
                    <SelectTrigger data-testid="signup-industry-select">
                      <SelectValue placeholder="Select your industry" />
                    </SelectTrigger>
                    <SelectContent>
                      {industries.map((industry) => (
                        <SelectItem key={industry} value={industry}>{industry}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Plan Selection */}
              <div className="space-y-3">
                <Label>Select Your Plan</Label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {plans.map((plan) => (
                    <div
                      key={plan.id}
                      className={cn(
                        "relative border-2 rounded-xl p-4 cursor-pointer transition-all",
                        formData.plan === plan.id 
                          ? "border-emerald-brand bg-emerald-brand/5" 
                          : "border-gray-border hover:border-gray-300"
                      )}
                      onClick={() => handleChange('plan', plan.id)}
                      data-testid={`plan-${plan.id}`}
                    >
                      {plan.popular && (
                        <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-emerald-brand text-white text-xs px-2 py-0.5 rounded-full">
                          Most Popular
                        </span>
                      )}
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-heading font-semibold">{plan.name}</h4>
                        {formData.plan === plan.id && (
                          <div className="w-5 h-5 rounded-full bg-emerald-brand flex items-center justify-center">
                            <Check className="h-3 w-3 text-white" />
                          </div>
                        )}
                      </div>
                      <p className="text-lg font-bold text-emerald-brand mb-2">{plan.price}</p>
                      <ul className="space-y-1">
                        {plan.features.map((feature, i) => (
                          <li key={i} className="text-xs text-gray-medium flex items-center gap-1">
                            <Check className="h-3 w-3 text-emerald-brand" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-emerald-brand hover:bg-emerald-hover"
                disabled={loading}
                data-testid="signup-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating account...
                  </>
                ) : (
                  'Start Free Trial'
                )}
              </Button>
            </form>

            <p className="mt-4 text-center text-sm text-gray-medium">
              Already have an account?{' '}
              <Link to="/login" className="text-emerald-brand hover:text-emerald-hover font-medium" data-testid="login-link">
                Sign in
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SignupPage;
